import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

/**
 * AuthCallback - Composant pour traiter le retour de Google OAuth
 * 
 * Version: 4.1.0
 * 
 * PROTECTIONS AJOUTÉES:
 * - Retry automatique en cas d'erreur réseau (3 tentatives)
 * - Timeout configurable
 * - Gestion des erreurs détaillée
 * - Bouton de retry manuel
 * - Logs de debug complets
 */

const MAX_RETRIES = 3;
const RETRY_DELAY = 1500; // ms
const REQUEST_TIMEOUT = 15000; // 15 secondes

const AuthCallback = ({ onAuthSuccess }) => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('processing'); // processing, error, success
  const [errorMessage, setErrorMessage] = useState('');
  const [retryCount, setRetryCount] = useState(0);

  const processGoogleCallback = async (sessionId, attempt = 1) => {
    console.log(`[AUTH CALLBACK] Tentative ${attempt}/${MAX_RETRIES}`);
    
    try {
      // Appel API avec timeout
      const response = await axios.post(
        `${API_URL}/api/auth/google/session`,
        { session_id: sessionId },
        { 
          withCredentials: true,
          timeout: REQUEST_TIMEOUT
        }
      );

      console.log('[AUTH CALLBACK] Réponse API:', response.status);

      if (response.data.success && response.data.user) {
        console.log('[AUTH CALLBACK] Connexion réussie:', response.data.user.email);
        setStatus('success');
        toast.success(`Bienvenue, ${response.data.user.name || response.data.user.email}!`);
        
        if (onAuthSuccess) {
          onAuthSuccess(response.data.user);
        }
        
        // Nettoyer l'URL et rediriger
        window.history.replaceState({}, document.title, '/dashboard');
        navigate('/dashboard', { 
          replace: true,
          state: { user: response.data.user }
        });
        return true;
      } else {
        throw new Error(response.data?.detail || 'Réponse invalide du serveur');
      }

    } catch (error) {
      console.error(`[AUTH CALLBACK] Erreur tentative ${attempt}:`, error.message);
      
      // Vérifier si c'est une erreur réseau (retry possible)
      const isNetworkError = !error.response || error.code === 'ECONNABORTED';
      
      if (isNetworkError && attempt < MAX_RETRIES) {
        console.log(`[AUTH CALLBACK] Erreur réseau, retry dans ${RETRY_DELAY}ms...`);
        setRetryCount(attempt);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return processGoogleCallback(sessionId, attempt + 1);
      }
      
      // Erreur finale
      let errorMsg = 'Erreur de connexion Google';
      
      if (error.response?.status === 401) {
        errorMsg = 'Session expirée. Veuillez réessayer.';
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error.code === 'ECONNABORTED') {
        errorMsg = 'Connexion trop lente. Vérifiez votre réseau.';
      } else if (!error.response) {
        errorMsg = 'Erreur réseau. Vérifiez votre connexion.';
      }
      
      console.error('[AUTH CALLBACK] Erreur finale:', errorMsg);
      setErrorMessage(errorMsg);
      setStatus('error');
      return false;
    }
  };

  const handleRetry = () => {
    setStatus('processing');
    setErrorMessage('');
    setRetryCount(0);
    hasProcessed.current = false;
    
    // Recharger la page pour réessayer
    window.location.reload();
  };

  const handleBackToLogin = () => {
    navigate('/', { replace: true });
  };

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const startAuth = async () => {
      // Extraire le session_id
      const hash = window.location.hash;
      console.log('[AUTH CALLBACK] Hash reçu:', hash ? hash.substring(0, 50) + '...' : 'vide');
      
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        console.error('[AUTH CALLBACK] Pas de session_id dans l\'URL');
        setErrorMessage('Session non trouvée. Veuillez réessayer.');
        setStatus('error');
        return;
      }

      const sessionId = sessionIdMatch[1];
      console.log('[AUTH CALLBACK] Session ID:', sessionId.substring(0, 20) + '...');
      
      await processGoogleCallback(sessionId);
    };

    startAuth();
  }, [navigate, onAuthSuccess]);

  // Écran d'erreur avec bouton retry
  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-emerald-50 p-4">
        <div className="flex flex-col items-center gap-6 max-w-sm text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          
          <div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Erreur de connexion
            </h2>
            <p className="text-gray-600 text-sm">
              {errorMessage}
            </p>
          </div>
          
          <div className="flex flex-col gap-3 w-full">
            <button
              onClick={handleRetry}
              className="w-full px-4 py-3 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 transition-colors"
            >
              Réessayer
            </button>
            <button
              onClick={handleBackToLogin}
              className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
            >
              Retour à la connexion
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Écran de chargement
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-emerald-50">
      <div className="flex flex-col items-center gap-4">
        <img 
          src="/logo.png" 
          alt="Leu Budget du Génie" 
          className="h-16 w-16 rounded-2xl shadow-md animate-pulse" 
        />
        <div className="text-lg font-medium text-gray-600">
          {retryCount > 0 ? `Nouvelle tentative (${retryCount}/${MAX_RETRIES})...` : 'Connexion en cours...'}
        </div>
        <div className="flex gap-1">
          <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
          <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
          <div className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;
